/**
 * Roles and permissions (F1): administration of role templates (`app_role`),
 * their assignments to accounts (`account_role`) and direct grants
 * `(scope, capability, action, allow/deny, inheritance)` (`permission_grant`).
 * The server recompiles `effective_grant` in the SAME transaction — the sync
 * of the affected parties changes instantly. Everything requires
 * `permissions:manage` (or `manage` on the granted capability) —
 * require_grant re-validates.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  applyRoleTemplate,
  getCapabilitiesQuery,
  getChurchesQuery,
  getGroupsQuery,
  getPeopleQuery,
  getVenuesQuery,
  patchRowVerified,
  queryKeys,
  ROLE_TEMPLATES,
  roleTemplateById,
  templateNeedsScope,
} from "@agape/shared";
import {
  account_role,
  type AccountRoleRecord,
  app_role,
  type AppRoleRecord,
  type CapabilityRecord,
  type ChurchGroupRecord,
  type ChurchRecord,
  eq,
  permission_grant,
  type PermissionGrantRecord,
  type PersonRecord,
  type VenueRecord,
} from "@agape/powersync";
import { KeyRound, Plus, ShieldCheck, Sparkles, Trash2, UserCog } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { Switch } from "@fleet/ui/switch";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function PermissionsPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );
  const capabilities = useDomainQuery<CapabilityRecord>(
    () => [...queryKeys.domain.catalogs(), "capabilities"],
    (db) => getCapabilitiesQuery(db),
  );
  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );
  const roles = useDomainQuery<AppRoleRecord>(
    (uid) => queryKeys.domain.roles(uid),
    (db) => db.select().from(app_role),
  );
  const assignments = useDomainQuery<AccountRoleRecord>(
    (uid) => queryKeys.domain.accountRoles(uid),
    (db) => db.select().from(account_role),
  );
  const grantRows = useDomainQuery<PermissionGrantRecord>(
    (uid) => queryKeys.domain.permissionGrants(uid),
    (db) => db.select().from(permission_grant),
  );

  // Active church of the panel.
  const [churchId, setChurchId] = createSignal("");
  const activeChurch = () => churchId() || (churches.data ?? [])[0]?.id || "";
  const canManage = () => grants.can([activeChurch()], "permissions", "manage");

  const churchRoles = createMemo(() =>
    (roles.data ?? []).filter((r) => r.church_id === activeChurch() && !r.deleted_at),
  );
  const churchAssignments = createMemo(() =>
    (assignments.data ?? []).filter((a) => a.church_id === activeChurch()),
  );
  const churchVenues = createMemo(() =>
    (venues.data ?? []).filter((v) => v.church_id === activeChurch()),
  );
  const churchGroups = createMemo(() =>
    (groups.data ?? []).filter((g) => g.church_id === activeChurch()),
  );
  const scopeIds = createMemo(() => {
    const ids = new Set<string>([activeChurch()]);
    for (const v of churchVenues()) {
      ids.add(v.id);
    }
    for (const g of churchGroups()) {
      ids.add(g.id);
    }
    return ids;
  });
  const churchGrants = createMemo(() =>
    (grantRows.data ?? []).filter((g) => g.scope_id && scopeIds().has(g.scope_id)),
  );
  // People with a linked account = assignable subjects.
  const accountPeople = createMemo(() => (people.data ?? []).filter((p) => p.account_user_id));

  const roleName = (id: string | null) =>
    (roles.data ?? []).find((r) => r.id === id)?.name ?? id ?? "";
  const subjectLabel = (kind: string | null, id: string | null) => {
    if (kind === "role") {
      return `${t(msg`Role`)}: ${roleName(id)}`;
    }
    const person = (people.data ?? []).find((p) => p.account_user_id === id);
    return person?.name ?? id ?? "";
  };
  const scopeLabel = (kind: string | null, id: string | null) => {
    if (kind === "venue") {
      return `${t(msg`Venue`)}: ${(venues.data ?? []).find((v) => v.id === id)?.name ?? id}`;
    }
    if (kind === "group") {
      return `${t(msg`Group`)}: ${(groups.data ?? []).find((g) => g.id === id)?.name ?? id}`;
    }
    if (kind === "org") {
      return t(msg`Whole organization`);
    }
    return `${t(msg`Church`)}: ${(churches.data ?? []).find((c) => c.id === id)?.name ?? id}`;
  };

  // ── Create role ───────────────────────────────────────────────────────────
  const [roleOpen, setRoleOpen] = createSignal(false);
  const [roleNameInput, setRoleNameInput] = createSignal("");
  const [roleDescription, setRoleDescription] = createSignal("");
  const createRole = async () => {
    if (!roleNameInput().trim()) {
      return;
    }
    await write()
      .insert(app_role)
      .values({
        id: newId(),
        church_id: activeChurch(),
        name: roleNameInput().trim(),
        description: roleDescription().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setRoleOpen(false);
    setRoleNameInput("");
    setRoleDescription("");
    showToast(t(msg`Role created`), "success");
  };
  const trashRole = async (id: string) => {
    try {
      await patchRowVerified(write(), app_role, app_role.id, id, { deleted_at: nowIso() });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  // ── Apply a role TEMPLATE (grants ready + adjustable afterwards) ──────────
  const [templateOpen, setTemplateOpen] = createSignal(false);
  const [templateId, setTemplateId] = createSignal(ROLE_TEMPLATES[0]?.id ?? "");
  const [templateGroup, setTemplateGroup] = createSignal("");
  const [templateVenue, setTemplateVenue] = createSignal("");
  const [templateUser, setTemplateUser] = createSignal("");
  const activeTemplate = () => roleTemplateById(templateId());
  const templateNeeds = createMemo(() => {
    const tpl = activeTemplate();
    return tpl ? templateNeedsScope(tpl) : { group: false, venue: false };
  });
  const applyTemplate = async () => {
    const tpl = activeTemplate();
    if (!tpl) {
      return;
    }
    const applied = applyRoleTemplate({
      template: tpl,
      churchId: activeChurch(),
      groupId: templateGroup() || null,
      venueId: templateVenue() || null,
      userId: templateUser() || null,
      newId,
      now: nowIso(),
    });
    const db = write();
    // The role and its grants travel as ONE group (same logical client
    // transaction); the server recompiles effective_grant at the end.
    await db.insert(app_role).values({
      id: applied.role.id,
      church_id: applied.role.church_id,
      name: applied.role.name,
      description: applied.role.description,
      created_at: applied.role.created_at,
      updated_at: applied.role.updated_at,
    });
    for (const g of applied.grants) {
      await db.insert(permission_grant).values({
        id: g.id,
        subject_kind: g.subject_kind,
        subject_id: g.subject_id,
        scope_kind: g.scope_kind,
        scope_id: g.scope_id,
        capability_id: g.capability_id,
        action: g.action,
        effect: g.effect,
        inherit: g.inherit,
        created_at: g.created_at,
        updated_at: g.updated_at,
      });
    }
    if (applied.assignment) {
      await db.insert(account_role).values({
        id: applied.assignment.id,
        role_id: applied.assignment.role_id,
        user_id: applied.assignment.user_id,
        created_at: applied.assignment.created_at,
        updated_at: applied.assignment.updated_at,
      });
    }
    setTemplateOpen(false);
    setTemplateGroup("");
    setTemplateVenue("");
    setTemplateUser("");
    showToast(
      applied.skipped.length > 0
        ? t(msg`Role created — some grants need a group/venue and were skipped`)
        : t(msg`Role created from template`),
      applied.skipped.length > 0 ? "info" : "success",
    );
  };

  // ── Assign role to account ────────────────────────────────────────────────
  const [assignOpen, setAssignOpen] = createSignal(false);
  const [assignRole, setAssignRole] = createSignal("");
  const [assignUser, setAssignUser] = createSignal("");
  const assign = async () => {
    if (!assignRole() || !assignUser()) {
      return;
    }
    await write().insert(account_role).values({
      id: newId(),
      role_id: assignRole(),
      user_id: assignUser(),
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setAssignOpen(false);
    showToast(t(msg`Role assigned`), "success");
  };
  const unassign = async (id: string) => {
    await write().delete(account_role).where(eq(account_role.id, id));
  };

  // ── Create grant ──────────────────────────────────────────────────────────
  const [grantOpen, setGrantOpen] = createSignal(false);
  const [gSubjectKind, setGSubjectKind] = createSignal<"user" | "role">("user");
  const [gSubject, setGSubject] = createSignal("");
  const [gScope, setGScope] = createSignal("");
  const [gCapability, setGCapability] = createSignal("");
  const [gAction, setGAction] = createSignal<string>("read");
  const [gDeny, setGDeny] = createSignal(false);
  const [gInherit, setGInherit] = createSignal(true);

  const scopeOptions = createMemo(() => [
    { value: `church:${activeChurch()}`, label: scopeLabel("church", activeChurch()) },
    ...churchVenues().map((v) => ({ value: `venue:${v.id}`, label: scopeLabel("venue", v.id) })),
    ...churchGroups().map((g) => ({ value: `group:${g.id}`, label: scopeLabel("group", g.id) })),
  ]);
  const subjectOptions = createMemo(() =>
    gSubjectKind() === "role"
      ? churchRoles().map((r) => ({ value: r.id, label: r.name ?? r.id }))
      : accountPeople().map((p) => ({
          value: p.account_user_id ?? "",
          label: `${p.name}`,
        })),
  );
  const createGrant = async () => {
    const [scopeKind, scopeId] = gScope().split(":");
    if (!gSubject() || !scopeId || !gCapability()) {
      return;
    }
    await write()
      .insert(permission_grant)
      .values({
        id: newId(),
        subject_kind: gSubjectKind(),
        subject_id: gSubject(),
        scope_kind: scopeKind,
        scope_id: scopeId,
        capability_id: gCapability(),
        action: gAction(),
        effect: gDeny() ? "deny" : "allow",
        inherit: gInherit() ? 1 : 0,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setGrantOpen(false);
    showToast(t(msg`Permission granted`), "success");
  };
  const removeGrant = async (id: string) => {
    await write().delete(permission_grant).where(eq(permission_grant.id, id));
  };

  return (
    <div class="space-y-6">
      <PageSubHeader title={t(msg`Roles & permissions`)} />
      <div class="max-w-sm">
        <Select
          options={(churches.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.id }))}
          value={activeChurch()}
          onChange={setChurchId}
          aria-label={t(msg`Church`)}
        />
      </div>
      <Show when={!canManage()}>
        <p class="text-sm text-muted-foreground" data-testid="permissions-readonly">
          {t(
            msg`You can see this panel but only permission managers of this church can change it.`,
          )}
        </p>
      </Show>

      {/* ── Roles plantilla ──────────────────────────────────────────────── */}
      <section class="space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Role templates`)}
          </h3>
          <Show when={canManage()}>
            <div class="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                data-testid="role-template-open"
                onClick={() => setTemplateOpen(true)}
              >
                <Sparkles size={14} /> {t(msg`From template`)}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid="role-create"
                onClick={() => setRoleOpen(true)}
              >
                <UserCog size={14} /> {t(msg`New role`)}
              </Button>
            </div>
          </Show>
        </div>
        <Show
          when={churchRoles().length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="roles-empty">
              {t(msg`No roles yet — e.g. Pastor, Cell leader, Musician…`)}
            </p>
          }
        >
          <For each={churchRoles()}>
            {(role) => (
              <Card data-testid="role-item">
                <CardContent class="flex items-center justify-between gap-3 py-2.5">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium">{role.name}</p>
                    <Show when={role.description}>
                      <p class="truncate text-xs text-muted-foreground">{role.description}</p>
                    </Show>
                  </div>
                  <Show when={canManage()}>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t(msg`Move role to trash`)}
                      onClick={() => void trashRole(role.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </Show>
                </CardContent>
              </Card>
            )}
          </For>
        </Show>
      </section>

      {/* ── Asignaciones ─────────────────────────────────────────────────── */}
      <section class="space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Role assignments`)}
          </h3>
          <Show when={canManage()}>
            <Button
              size="sm"
              variant="ghost"
              data-testid="assignment-create"
              onClick={() => setAssignOpen(true)}
            >
              <Plus size={14} /> {t(msg`Assign role`)}
            </Button>
          </Show>
        </div>
        <Show
          when={churchAssignments().length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No assignments yet.`)}</p>}
        >
          <For each={churchAssignments()}>
            {(a) => (
              <Card data-testid="assignment-item">
                <CardContent class="flex items-center justify-between gap-3 py-2.5">
                  <p class="min-w-0 truncate text-sm">
                    <span class="font-medium">{subjectLabel("user", a.user_id)}</span>{" "}
                    <span class="text-muted-foreground">→ {roleName(a.role_id)}</span>
                  </p>
                  <Show when={canManage()}>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t(msg`Remove assignment`)}
                      onClick={() => void unassign(a.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </Show>
                </CardContent>
              </Card>
            )}
          </For>
        </Show>
      </section>

      {/* ── Concesiones ──────────────────────────────────────────────────── */}
      <section class="space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Grants (scope × capability × action)`)}
          </h3>
          <Show when={canManage()}>
            <Button size="sm" data-testid="grant-create" onClick={() => setGrantOpen(true)}>
              <KeyRound size={14} /> {t(msg`New grant`)}
            </Button>
          </Show>
        </div>
        <Show
          when={churchGrants().length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="grants-empty">
              {t(msg`No direct grants on this church yet.`)}
            </p>
          }
        >
          <For each={churchGrants()}>
            {(g) => (
              <Card data-testid="grant-item" data-grant-capability={g.capability_id}>
                <CardContent class="flex items-center justify-between gap-3 py-2.5">
                  <div class="min-w-0">
                    <p class="truncate text-sm">
                      <span class="font-medium">{subjectLabel(g.subject_kind, g.subject_id)}</span>{" "}
                      <span
                        class={
                          g.effect === "deny"
                            ? "font-semibold text-destructive"
                            : "text-muted-foreground"
                        }
                      >
                        {g.effect === "deny" ? t(msg`DENIED`) : ""} {g.capability_id}:{g.action}
                      </span>
                    </p>
                    <p class="truncate text-xs text-muted-foreground">
                      {scopeLabel(g.scope_kind, g.scope_id)}
                      {g.inherit ? ` · ${t(msg`inherits to descendants`)}` : ""}
                    </p>
                  </div>
                  <Show when={canManage()}>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t(msg`Revoke grant`)}
                      data-testid="grant-revoke"
                      onClick={() => void removeGrant(g.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </Show>
                </CardContent>
              </Card>
            )}
          </For>
        </Show>
      </section>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={templateOpen()}
        onOpenChange={setTemplateOpen}
        title={t(msg`Apply a role template`)}
        icon={<Sparkles size={20} />}
        submitLabel={t(msg`Create role`)}
        submitTestId="role-template-submit"
        disabled={
          !templateId() ||
          (templateNeeds().group && !templateGroup()) ||
          (templateNeeds().venue && !templateVenue())
        }
        onSubmit={applyTemplate}
      >
        <Field id="tpl-role" label={t(msg`Template`)}>
          <Select
            options={ROLE_TEMPLATES.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
            value={templateId()}
            onChange={setTemplateId}
            aria-label={t(msg`Template`)}
          />
        </Field>
        <Show when={activeTemplate()?.description}>
          <p class="text-xs text-muted-foreground" data-testid="role-template-description">
            {activeTemplate()?.description}
          </p>
        </Show>
        <Show when={templateNeeds().group}>
          <Field id="tpl-group" label={t(msg`Group (this template grants on a group)`)}>
            <Select
              options={churchGroups().map((g) => ({ value: g.id, label: g.name ?? g.id }))}
              value={templateGroup()}
              onChange={setTemplateGroup}
              aria-label={t(msg`Group`)}
              emptyLabel={t(msg`Create a group first`)}
            />
          </Field>
        </Show>
        <Show when={templateNeeds().venue}>
          <Field id="tpl-venue" label={t(msg`Venue (this template grants on a venue)`)}>
            <Select
              options={churchVenues().map((v) => ({ value: v.id, label: v.name ?? v.id }))}
              value={templateVenue()}
              onChange={setTemplateVenue}
              aria-label={t(msg`Venue`)}
              emptyLabel={t(msg`Create a venue first`)}
            />
          </Field>
        </Show>
        <Field id="tpl-user" label={t(msg`Assign to an account (optional)`)}>
          <Select
            options={[
              { value: "", label: t(msg`Don't assign yet`) },
              ...accountPeople().map((p) => ({
                value: p.account_user_id ?? "",
                label: p.name ?? p.id,
              })),
            ]}
            value={templateUser()}
            onChange={setTemplateUser}
            aria-label={t(msg`Account`)}
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={roleOpen()}
        onOpenChange={setRoleOpen}
        title={t(msg`New role template`)}
        icon={<UserCog size={20} />}
        submitTestId="role-submit"
        disabled={!roleNameInput().trim()}
        onSubmit={createRole}
      >
        <Field
          id="role-name"
          label={t(msg`Name`)}
          value={roleNameInput()}
          onInput={setRoleNameInput}
          placeholder={t(msg`Pastor, Cell leader, Projection director…`)}
          testId="role-name-input"
        />
        <Field
          id="role-description"
          label={t(msg`Description`)}
          value={roleDescription()}
          onInput={setRoleDescription}
        />
      </FormDialog>

      <FormDialog
        open={assignOpen()}
        onOpenChange={setAssignOpen}
        title={t(msg`Assign role`)}
        icon={<ShieldCheck size={20} />}
        submitTestId="assignment-submit"
        disabled={!assignRole() || !assignUser()}
        onSubmit={assign}
      >
        <Field id="as-role" label={t(msg`Role`)}>
          <Select
            options={churchRoles().map((r) => ({ value: r.id, label: r.name ?? r.id }))}
            value={assignRole()}
            onChange={setAssignRole}
            aria-label={t(msg`Role`)}
            emptyAction={{
              label: t(msg`Create a role first`),
              onSelect: () => {
                setAssignOpen(false);
                setRoleOpen(true);
              },
            }}
          />
        </Field>
        <Field id="as-user" label={t(msg`Account (person with linked account)`)}>
          <Select
            options={accountPeople().map((p) => ({
              value: p.account_user_id ?? "",
              label: p.name ?? p.id,
            }))}
            value={assignUser()}
            onChange={setAssignUser}
            aria-label={t(msg`Account`)}
            emptyLabel={t(msg`Link an account to a person in the directory first`)}
          />
        </Field>
        <Field
          id="as-user-manual"
          label={t(msg`…or paste a user id`)}
          value={assignUser()}
          onInput={setAssignUser}
          testId="assignment-user-input"
        />
      </FormDialog>

      <FormDialog
        open={grantOpen()}
        onOpenChange={setGrantOpen}
        title={t(msg`New grant`)}
        icon={<KeyRound size={20} />}
        submitTestId="grant-submit"
        disabled={!gSubject() || !gScope() || !gCapability()}
        onSubmit={createGrant}
      >
        <div class="grid grid-cols-2 gap-3">
          <Field id="g-subject-kind" label={t(msg`Subject`)}>
            <Select<"user" | "role">
              options={[
                { value: "user", label: t(msg`Account`) },
                { value: "role", label: t(msg`Role`) },
              ]}
              value={gSubjectKind()}
              onChange={(v) => {
                setGSubjectKind(v);
                setGSubject("");
              }}
              aria-label={t(msg`Subject`)}
            />
          </Field>
          <Field id="g-subject" label={gSubjectKind() === "role" ? t(msg`Role`) : t(msg`Account`)}>
            <Select
              options={subjectOptions()}
              value={gSubject()}
              onChange={setGSubject}
              aria-label={t(msg`Subject`)}
              emptyLabel={t(msg`No subjects available`)}
            />
          </Field>
        </div>
        <Show when={gSubjectKind() === "user"}>
          <Field
            id="g-subject-manual"
            label={t(msg`…or paste a user id`)}
            value={gSubject()}
            onInput={setGSubject}
            testId="grant-user-input"
          />
        </Show>
        <Field id="g-scope" label={t(msg`Scope (tree node)`)}>
          <Select
            options={scopeOptions()}
            value={gScope()}
            onChange={setGScope}
            aria-label={t(msg`Scope`)}
          />
        </Field>
        <div class="grid grid-cols-2 gap-3">
          <Field id="g-capability" label={t(msg`Capability`)}>
            <Select
              options={(capabilities.data ?? []).map((c) => ({
                value: c.id,
                label: c.name ?? c.id,
              }))}
              value={gCapability()}
              onChange={setGCapability}
              aria-label={t(msg`Capability`)}
            />
          </Field>
          <Field id="g-action" label={t(msg`Action`)}>
            <Select
              options={[
                { value: "read", label: t(msg`Read`) },
                { value: "write", label: t(msg`Write`) },
                { value: "manage", label: t(msg`Manage`) },
              ]}
              value={gAction()}
              onChange={setGAction}
              aria-label={t(msg`Action`)}
            />
          </Field>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span class="text-sm">{t(msg`Deny (explicit)`)}</span>
            <Switch checked={gDeny()} onCheckedChange={setGDeny} aria-label={t(msg`Deny`)} />
          </div>
          <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <span class="text-sm">{t(msg`Inherit to descendants`)}</span>
            <Switch
              checked={gInherit()}
              onCheckedChange={setGInherit}
              aria-label={t(msg`Inherit`)}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
